import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useSubmitFeedback } from '@/hooks/useFeedback';

const createSchema = (t: (key: string) => string) =>
  z.object({
    type: z.enum(['bug', 'feedback', 'praise']),
    title: z.string().min(3, t('feedbackDialog.validation.titleMin')).max(100, t('feedbackDialog.validation.titleMax')),
    description: z.string().max(2000, t('feedbackDialog.validation.descriptionMax')).optional(),
    severity: z.enum(['low', 'medium', 'high', 'critical']).optional(),
  });

/** Handgeschrieben statt `z.infer<ReturnType<typeof createSchema>>`, und das ist
 *  keine Bequemlichkeit: dieses Projekt kompiliert mit `strict: false` und damit
 *  ohne `strictNullChecks`. Dort ist `undefined extends string` WAHR, und zods
 *  `addQuestionMarks` markiert deshalb JEDE Eigenschaft als optional — auch
 *  `type` und `title`, die das Schema verpflichtend fuehrt. Die abgeleitete Form
 *  passte damit nicht mehr auf `SubmitFeedbackInput` (Pflichtfeld kann nicht von
 *  einer optionalen Eigenschaft bedient werden). Diese Form MUSS zum Schema oben
 *  passen; wer dort ein Feld ergaenzt oder seine Optionalitaet aendert, zieht sie
 *  hier nach. Der `parse`-Pin unter der Deklaration faengt eine Abweichung im
 *  Typ ab, solange sie ausdrueckbar ist. */
type FormValues = {
  type: 'bug' | 'feedback' | 'praise';
  title: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
};

/** Drift-Pin: `Required<>` auf beiden Seiten raeumt genau den Unterschied weg,
 *  den `strictNullChecks: false` erzeugt (die Optionalitaet), und vergleicht dann
 *  Schluesselmenge und Werttypen in BEIDE Richtungen. Faengt ein im Schema
 *  ergaenztes, entferntes oder umbenanntes Feld und geaenderte Enum-Werte;
 *  faengt bewusst NICHT eine reine Optionalitaets-Aenderung, die ist unter
 *  dieser Compiler-Einstellung nicht ausdrueckbar.
 *
 *  Rein typseitig: `AssertTrue<T extends true>` erzeugt KEIN Statement, das im
 *  Bundle landet — die frueheren zwei Zeilen (`const … = true; void …;`) waren
 *  wirkungslos, aber echter Modul-Code. Der Abweichungsfall liefert bewusst
 *  `false` und nicht `never`: `never extends true` ist WAHR, ein
 *  `never`-Ergebnis wuerde die Schranke also klaglos passieren und der Pin
 *  waere ein Placebo. */
type ExactExceptOptionality<A, B> = [Required<A>] extends [Required<B>]
  ? ([Required<B>] extends [Required<A>] ? true : false)
  : false;
type AssertTrue<T extends true> = T;
type FormValuesMatchSchema = AssertTrue<
  ExactExceptOptionality<FormValues, z.infer<ReturnType<typeof createSchema>>>
>;

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  defaultType?: 'bug' | 'feedback' | 'praise';
}

const typeKeys = {
  bug: 'bug',
  feedback: 'feedback',
  praise: 'praise',
};

export function FeedbackDialog({ open, onClose, defaultType = 'feedback' }: FeedbackDialogProps) {
  const { t } = useTranslation('roadmap');
  const location = useLocation();
  const submitMutation = useSubmitFeedback();

  const schema = createSchema(t);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: defaultType,
      title: '',
      description: '',
      severity: 'medium',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({ type: defaultType, title: '', description: '', severity: 'medium' });
    }
  }, [open, defaultType]);

  const currentType = form.watch('type');
  const typeKey = typeKeys[currentType];

  const onSubmit = async (values: FormValues) => {
    try {
      await submitMutation.mutateAsync({
        ...values,
        page_url: location.pathname,
      });
    } catch {
      // A7O-16: Der Fehler-Toast kommt aus useSubmitFeedback (onError). Hier nur
      // nicht weiterwerfen, sonst wird er zur unbehandelten Ablehnung; der
      // Dialog bleibt offen, damit nichts Getipptes verloren geht.
      return;
    }
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`feedbackDialog.titles.${typeKey}`)}</DialogTitle>
          <DialogDescription>{t(`feedbackDialog.descriptions.${typeKey}`)}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('feedbackDialog.typeLabel')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="bug">{t('feedbackDialog.typeOptions.bug')}</SelectItem>
                      <SelectItem value="feedback">{t('feedbackDialog.typeOptions.feedback')}</SelectItem>
                      <SelectItem value="praise">{t('feedbackDialog.typeOptions.praise')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('feedbackDialog.titleLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        currentType === 'bug'
                          ? t('feedbackDialog.titlePlaceholderBug')
                          : t('feedbackDialog.titlePlaceholderOther')
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {currentType === 'bug' ? t('feedbackDialog.stepsLabel') : t('feedbackDialog.detailsLabel')} {t('feedbackDialog.optional')}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={
                        currentType === 'bug'
                          ? t('feedbackDialog.stepsPlaceholder')
                          : t('feedbackDialog.detailsPlaceholder')
                      }
                      className="resize-none"
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {currentType === 'bug' && (
              <FormField
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('feedbackDialog.severityLabel')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">{t('feedbackDialog.severities.low')}</SelectItem>
                        <SelectItem value="medium">{t('feedbackDialog.severities.medium')}</SelectItem>
                        <SelectItem value="high">{t('feedbackDialog.severities.high')}</SelectItem>
                        <SelectItem value="critical">{t('feedbackDialog.severities.critical')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('feedbackDialog.cancel')}
              </Button>
              <Button type="submit" disabled={submitMutation.isPending}>
                {submitMutation.isPending ? t('feedbackDialog.submitting') : t('feedbackDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
